import { Component, ChangeDetectorRef } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from './auth.service';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  template: `
    <div class="auth-container">
      <div class="scryber-icon-row">
        <img src="/scryber-logo-lime.png?v=4" alt="Scryber" />
      </div>
      <div class="auth-card">
        <a routerLink="/login" class="back-link">← Back</a>
        <div class="auth-header">
          <h1>Forgot your password?</h1>
          <p class="auth-subtitle">Enter your email and we'll send you a link to reset your password.</p>
        </div>

        @if (errorMessage) {
          <div class="auth-error" role="alert">
            {{ errorMessage }}
          </div>
        }
        @if (successMessage) {
          <div class="auth-success" role="status">
            {{ successMessage }}
          </div>
        }

        @if (!successMessage) {
          <form [formGroup]="form" (ngSubmit)="onSubmit()" class="auth-form">
            <div class="form-group">
              <label for="email">Email</label>
              <input id="email" type="email" formControlName="email" placeholder="you@example.com" autocomplete="email" />
              @if (form.get('email')?.invalid && form.get('email')?.touched) {
                <span class="field-error">Valid email is required</span>
              }
            </div>
            <button type="submit" class="auth-button primary" [disabled]="form.invalid || loading">
              {{ loading ? 'Sending…' : 'Send reset link' }}
              <span class="btn-arrow">→</span>
            </button>
          </form>
        }

        <div class="auth-footer">
          <p>Remember your password? <a routerLink="/login" class="auth-link">Sign in</a></p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .auth-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      min-height: 100vh;
      background: linear-gradient(160deg, var(--scryber-rail-from) 0%, var(--scryber-rail-to) 55%, #022c22 100%);
      padding: 0 0 20px;
    }
    .auth-card {
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 40px rgba(6, 78, 59, 0.25);
      padding: 40px;
      width: calc(100% - 40px);
      max-width: 400px;
      margin: auto;
    }
    .back-link {
      display: inline-block;
      color: var(--scryber-green-600);
      font-size: 14px;
      font-weight: 500;
      text-decoration: none;
      margin-bottom: 20px;
    }
    .back-link:hover { text-decoration: underline; }
    .auth-header h1 {
      color: #2d3748;
      font-size: 24px;
      font-weight: 700;
      margin: 0 0 8px 0;
    }
    .auth-subtitle {
      color: #718096;
      font-size: 14px;
      margin: 0 0 24px 0;
      line-height: 1.5;
    }
    .auth-error {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 14px;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #b91c1c;
      font-size: 14px;
      margin-bottom: 20px;
    }
    .auth-success {
      padding: 12px 14px;
      background: #f0fdf4;
      border: 1px solid #bbf7d0;
      border-radius: 8px;
      color: #166534;
      font-size: 14px;
      margin-bottom: 20px;
    }
    .auth-form { display: flex; flex-direction: column; gap: 18px; }
    .form-group { display: flex; flex-direction: column; gap: 6px; }
    .form-group label { color: #374151; font-size: 14px; font-weight: 500; }
    .form-group input {
      padding: 10px 12px;
      font-size: 15px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      background: white;
      color: #1f2937;
    }
    .form-group input:focus {
      outline: none;
      border-color: var(--scryber-green-600);
      box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.25);
    }
    .field-error { font-size: 12px; color: #b91c1c; }
    .auth-button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      padding: 12px 24px;
      font-size: 16px;
      font-weight: 600;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-top: 4px;
    }
    .auth-button.primary {
      background: linear-gradient(135deg, var(--scryber-green-600) 0%, var(--scryber-rail-to) 100%);
      color: white;
    }
    .auth-button.primary:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 8px 25px rgba(5, 150, 105, 0.4);
    }
    .auth-button:disabled { opacity: 0.7; cursor: not-allowed; }
    .btn-arrow { font-size: 18px; }
    .auth-footer {
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
    }
    .auth-footer p { color: #718096; font-size: 14px; margin: 0; }
    .auth-link { color: var(--scryber-green-600); font-weight: 500; text-decoration: none; }
    .auth-link:hover { text-decoration: underline; }
    @media (max-width: 480px) { .auth-card { padding: 24px; } }
  `],
})
export class ForgotPasswordComponent {
  form: FormGroup;
  loading = false;
  errorMessage = '';
  successMessage = '';

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  onSubmit(): void {
    if (this.form.invalid || this.loading) return;
    this.errorMessage = '';
    this.successMessage = '';
    this.loading = true;
    const defaultSuccess = 'If an account exists for that email, we\'ve sent a reset link. Check your inbox.';
    this.auth.forgotPassword(this.form.get('email')!.value).subscribe({
      next: (r) => {
        this.loading = false;
        const msg = r && typeof (r as any).message === 'string' ? String((r as any).message).trim() : '';
        if (r && (r as any).success !== false) {
          this.successMessage = msg || defaultSuccess;
        } else {
          this.errorMessage = msg || 'Something went wrong. Please try again.';
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.loading = false;
        const body = err && (err as any).error;
        const msg = (body && typeof body === 'object' && body.message) || (typeof body === 'string' ? body : null) || err?.message;
        this.errorMessage = msg && String(msg).trim() ? String(msg).trim() : 'Something went wrong. Please try again or contact support.';
        this.cdr.markForCheck();
      },
      complete: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }
}
